import { useState, useEffect, useCallback } from "react";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function useListing(fetchService, deleteService, pageSize = 10) {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ column: "", direction: "asc" });

  const debouncedSearch = useDebounce(searchQuery, 600);

  const fetchData = useCallback(async (page = 1) => {
  try {
    setLoader(true);

    const params = {
      page,
      limit: pageSize,
      ...(debouncedSearch && { q: debouncedSearch }),
      ...(sortConfig.column && { sortBy: sortConfig.column }),
      ...(sortConfig.column && { sortOrder: sortConfig.direction }),
    };

    const res = await fetchService(params);

    setData(res?.data || []);
    setPagination({
      page: res.meta.page,
      total: res.meta.total
    });
  } finally {
    setLoader(false);
  }
}, [debouncedSearch, sortConfig, pageSize, fetchService]);



  useEffect(() => {
    fetchData(pagination.page);
  }, [fetchData, pagination.page]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  const deleteItem = async (id) => {
    const res = await deleteService(id);

    if (res?.status === "success") {
      setData(prev => prev.filter(item => item.id !== id));

      // Wait for state update then check if page is empty
      setTimeout(() => {
        setData(current => {
          if (current.length === 0 && pagination.page > 1) {
            const newPage = pagination.page - 1;
            setPagination(prev => ({ ...prev, page: newPage }));
            fetchData(newPage); // Force fetch
          } else {
            fetchData(pagination.page); // Refresh same page
          }
          return current;
        });
      }, 0);
    }

    return res;
  };



const sortData = (column) => {
  let direction = "asc";

  if (sortConfig.column === column && sortConfig.direction === "asc") {
    direction = "desc";
  }

  setSortConfig({ column, direction });

  // Go back to first page when sorting changes
  setPagination(prev => ({ ...prev, page: 1 }));
};


  return {
    data,
    loader,
    pagination,
    setPagination,
    searchQuery,
    setSearchQuery,
    deleteItem,
    sortData,
    sortConfig,
    totalPages: Math.ceil((pagination.total || 1) / pageSize),
  };
}
